import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { campaignJobBoardQueryKeys } from "./queryKeys";

import { campaignQueryKeys } from "@/features/campaigns/api/queryKeys";
import { homeQueryKeys } from "@/features/home/api/queryKeys";
import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { PagedResponse } from "@/types/response/PagedResponse";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

type UseSetCampaignIntegrationPausedMutationParams = {
  campaignId: string;
  clientId?: string;
  clientIntegrationModuleId: string;
};

type SetCampaignIntegrationPausedRequest = Pick<CampaignJobBoardType, "isPaused">;

/** The endpoint echoes the resulting state rather than the whole integration. */
type SetCampaignIntegrationPausedResponse = SetCampaignIntegrationPausedRequest;

type Snapshot<TData> = [QueryKey, TData | undefined][];

type SetCampaignIntegrationPausedContext = {
  previousDetail?: CampaignJobBoardType;
  previousLists: Snapshot<PagedResponse<CampaignJobBoardType>>;
  previousInfinites: Snapshot<InfiniteData<PagedResponse<CampaignJobBoardType>>>;
};

export function useSetCampaignIntegrationPausedMutation({
  campaignId,
  clientId,
  clientIntegrationModuleId,
}: UseSetCampaignIntegrationPausedMutationParams) {
  const queryClient = useQueryClient();

  const detailKey = campaignJobBoardQueryKeys.detail(
    campaignId,
    clientIntegrationModuleId,
    clientId
  );

  // Prefix filters rather than exact keys: the list and infinite keys also carry the selected status
  // filter and search term, so an exact key would silently miss the one query the card the user just
  // clicked is actually rendered from, and the chip would not move until the refetch landed.
  const listsFilter = { queryKey: campaignJobBoardQueryKeys.lists() };
  const infinitesFilter = { queryKey: campaignJobBoardQueryKeys.infinites() };

  const withIsPaused = (item: CampaignJobBoardType, isPaused: boolean) =>
    item.clientIntegrationModuleId === clientIntegrationModuleId
      ? { ...item, isPaused }
      : item;

  return useMutation<
    SetCampaignIntegrationPausedResponse,
    AxiosError<ValidationErrorResponse>,
    SetCampaignIntegrationPausedRequest,
    SetCampaignIntegrationPausedContext
  >({
    mutationFn: ({ isPaused }) =>
      instance
        .put<SetCampaignIntegrationPausedResponse>(
          `/api/v2/Campaigns/${campaignId}/Integrations/${clientIntegrationModuleId}/Paused`,
          { isPaused, clientId2: clientId }
        )
        .then((response) => response.data),
    onMutate: async ({ isPaused }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey }),
        queryClient.cancelQueries(listsFilter),
        queryClient.cancelQueries(infinitesFilter),
      ]);

      const previousDetail = queryClient.getQueryData<CampaignJobBoardType>(detailKey);
      const previousLists =
        queryClient.getQueriesData<PagedResponse<CampaignJobBoardType>>(listsFilter);
      const previousInfinites = queryClient.getQueriesData<
        InfiniteData<PagedResponse<CampaignJobBoardType>>
      >(infinitesFilter);

      if (previousDetail) {
        queryClient.setQueryData<CampaignJobBoardType>(detailKey, {
          ...previousDetail,
          isPaused,
        });
      }

      queryClient.setQueriesData<PagedResponse<CampaignJobBoardType>>(
        listsFilter,
        (current) =>
          current
            ? {
              ...current,
              data: current.data.map((item) => withIsPaused(item, isPaused)),
            }
            : current
      );

      queryClient.setQueriesData<InfiniteData<PagedResponse<CampaignJobBoardType>>>(
        infinitesFilter,
        (current) =>
          current
            ? {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                data: page.data.map((item) => withIsPaused(item, isPaused)),
              })),
            }
            : current
      );

      return { previousDetail, previousLists, previousInfinites };
    },
    // Restoring every snapshot is the revert-on-failure requirement: a rejected pause puts the chip
    // back to the state the server still holds instead of leaving the UI claiming something the
    // integration never did.
    onError: (_error, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData<CampaignJobBoardType>(detailKey, context.previousDetail);
      }

      context?.previousLists.forEach(([key, data]) => queryClient.setQueryData(key, data));
      context?.previousInfinites.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      // Campaign integration queries carry isPaused directly; campaign queries embed integrations
      // when asked with includeIntegrations. Unlike the edit mutation this deliberately leaves the
      // `months`, `clientBudgetMonth` and invoice-transaction caches alone — pausing does not touch
      // allocation (AC 3), so refetching them would be work for a value that cannot have changed.
      queryClient.invalidateQueries({ queryKey: campaignJobBoardQueryKeys.all, exact: false });
      queryClient.invalidateQueries({ queryKey: campaignQueryKeys.all, exact: false });
      queryClient.invalidateQueries({ queryKey: [...homeQueryKeys.all, "active-campaign-distribution"], exact: false });
      queryClient.invalidateQueries({ queryKey: [...homeQueryKeys.all, "active-campaign-integration-count"], exact: false });
      queryClient.invalidateQueries({ queryKey: segmentsQueryKeys.all, exact: false });
    },
  });
}
